export interface ObsStreamInput {
  id?: string
  name: string
  egressUrl: string
  sourceRole?: 'camera' | 'programme'
}

interface ObsSource {
  prev_ver: number
  name: string
  uuid: string
  versioned_id: string
  id: string
  settings: Record<string, unknown>
  flags: number
  volume: number
  balance: number
  mixers: number
  sync: number
  enabled: boolean
  muted: boolean
  push_to_mute: boolean
  push_to_mute_delay: number
  push_to_talk: boolean
  push_to_talk_delay: number
  monitoring_type: number
  private_settings: Record<string, unknown>
}

function uuid(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const h = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

export function obsFileStem(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '').slice(0, 80) || 'event'
}

function computeGrid(count: number): { cols: number; rows: number } {
  const cols = Math.max(1, Math.ceil(Math.sqrt(count)))
  const rows = Math.max(1, Math.ceil(count / cols))
  return { cols, rows }
}

function makeMediaSource(name: string, egressUrl: string): ObsSource {
  return {
    prev_ver: 503316480,
    name,
    uuid: uuid(),
    versioned_id: 'ffmpeg_source',
    id: 'ffmpeg_source',
    settings: {
      input: egressUrl,
      input_format: 'mpegts',
      is_local_file: false,
      buffering_mb: 0,
      clear_on_media_end: false,
      close_when_inactive: true,
      reconnect_delay_sec: 1,
      restart_on_activate: true,
      hw_decode: false,
    },
    flags: 0,
    volume: 1.0,
    balance: 0.5,
    mixers: 1,
    sync: 0,
    enabled: true,
    muted: false,
    push_to_mute: false,
    push_to_mute_delay: 0,
    push_to_talk: false,
    push_to_talk_delay: 0,
    monitoring_type: 0,
    private_settings: {},
  }
}

interface SceneItem {
  name: string
  pos: { x: number; y: number }
  bounds: { x: number; y: number }
  bounds_type: number
  bounds_align: number
  align: number
  scale: { x: number; y: number }
  scale_filter: string
  crop_left: number
  crop_right: number
  crop_top: number
  crop_bottom: number
  rot: number
  visible: boolean
  locked: boolean
  id: number
}

function makeSceneItem(
  name: string,
  x: number,
  y: number,
  width: number,
  height: number,
  id: number
): SceneItem {
  return {
    name,
    pos: { x, y },
    bounds: { x: width, y: height },
    bounds_type: 2,
    bounds_align: 0,
    align: 5,
    scale: { x: 1.0, y: 1.0 },
    scale_filter: 'bilinear',
    crop_left: 0,
    crop_right: 0,
    crop_top: 0,
    crop_bottom: 0,
    rot: 0.0,
    visible: true,
    locked: false,
    id,
  }
}

function makeSceneSource(name: string, items: SceneItem[]): ObsSource {
  return {
    prev_ver: 503316480,
    name,
    uuid: uuid(),
    versioned_id: 'scene',
    id: 'scene',
    settings: {
      items,
      id_counter: items.length + 1,
      custom_size: false,
    },
    flags: 0,
    volume: 1.0,
    balance: 0.5,
    mixers: 0,
    sync: 0,
    enabled: true,
    muted: false,
    push_to_mute: false,
    push_to_mute_delay: 0,
    push_to_talk: false,
    push_to_talk_delay: 0,
    monitoring_type: 0,
    private_settings: {},
  }
}

export function generateObsSceneCollection(
  eventName: string,
  streams: ObsStreamInput[]
): Record<string, unknown> {
  const canvasWidth = 1920
  const canvasHeight = 1080

  // The returned show can never be an input to its own mix.
  const cameras = streams.filter((stream) => stream.sourceRole !== 'programme' && stream.egressUrl)
  const names = cameras.map((stream, index) => {
    const label = stream.name.replace(/[\r\n\t]/g, ' ').trim() || 'Camera'
    const identity = stream.id || String(index + 1)
    return {
      source: `Camera · ${label} · ${identity}`,
      silent: `Silent · ${label} · ${identity}`,
      scene: `View · ${label} · ${identity}`,
    }
  })
  const mediaSources = cameras.map((stream, index) => makeMediaSource(names[index].source, stream.egressUrl))
  // OBS mutes are source-wide, so the grid uses separate silent media sources.
  // Solo scenes retain their camera's audio; switching to the grid cannot sum
  // every open microphone. Inactive sources close their network connections.
  const silentSources = cameras.map((stream, index) => ({
    ...makeMediaSource(names[index].silent, stream.egressUrl), muted: true, mixers: 0,
  }))
  const soloScenes = names.map((name) => makeSceneSource(name.scene, [
    makeSceneItem(name.source, 0, 0, canvasWidth, canvasHeight, 1),
  ]))
  const gridName = 'All cameras · silent'
  const { cols, rows } = computeGrid(cameras.length)
  const gridItems = names.map((name, index) => makeSceneItem(
    name.silent,
    (index % cols) * canvasWidth / cols,
    Math.floor(index / cols) * canvasHeight / rows,
    canvasWidth / cols, canvasHeight / rows, index + 1,
  ))
  const allScenes = [...soloScenes, makeSceneSource(gridName, gridItems)]
  const firstScene = soloScenes[0]?.name || gridName

  return {
    name: `${eventName} - StreamBridge`,
    current_scene: firstScene,
    current_program_scene: firstScene,
    scene_order: allScenes.map((scene) => ({ name: scene.name })),
    sources: [...mediaSources, ...silentSources, ...allScenes],
    groups: [],
    quick_transitions: [],
    transitions: [],
    saved_projectors: [],
  }
}

export function downloadObsSceneCollection(
  eventName: string,
  streams: ObsStreamInput[]
): void {
  const collection = generateObsSceneCollection(eventName, streams)
  const json = JSON.stringify(collection, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${obsFileStem(eventName)}-obs-scenes.json`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
